import { useCallback, useEffect, useRef, useState } from "react";
import { posistDeltaActiveRun } from "../../../Admin/Store/api";
import { finishedCountOf } from "../../../../utils/Functions/posistSync";

// A store takes 35-70s (Posist returns the whole menu per store regardless of
// the sync window), so polling faster than this just burns requests. Same
// figure the sync modal uses, for the same reason.
const ACTIVE_POLL_MS = 4000;

// Nothing is running, so this is only asking "has someone started one". The
// dashboard is the screen people leave open all day, and the probe is a single
// indexed query, but four seconds of that forever is noise for no one.
const IDLE_POLL_MS = 30000;

const useSyncProgress = () => {
  const [run, setRun] = useState(null);
  const [otherActiveRuns, setOtherActiveRuns] = useState(0);

  // Refs, not state: the poll loop reads them across timeouts and must not
  // re-subscribe (or capture a stale value) every render.
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // setTimeout chain rather than setInterval: a slow probe must not have a
  // second one stacked behind it.
  const poll = useCallback(async () => {
    if (stoppedRef.current) return;

    // A dashboard on a second monitor or a background tab is still mounted and
    // still polling. Nothing is being looked at, so skip the request and come
    // back on the slow timer - the visibility listener below pulls it straight
    // back the moment the tab is in front again.
    if (typeof document !== "undefined" && document.hidden) {
      timerRef.current = setTimeout(poll, IDLE_POLL_MS);
      return;
    }

    const { status, body } = await posistDeltaActiveRun();

    if (stoppedRef.current) return;

    // No manage_store. The panel is not for this admin, and asking again on
    // every tick would just be a 403 every few seconds for as long as the
    // dashboard is open.
    if (status === 401 || status === 403) {
      setRun(null);
      stoppedRef.current = true;
      return;
    }

    let nextDelay = IDLE_POLL_MS;

    if (body?.status_code === 200) {
      // `active: false` is the normal answer, and it is what takes the panel
      // away the moment the last store finishes - the run is not kept on
      // screen afterwards.
      const active = body?.data?.active ? body?.data?.run : null;

      setRun(active);
      setOtherActiveRuns(body?.data?.other_active_runs ?? 0);

      if (active) nextDelay = ACTIVE_POLL_MS;
    }
    // Anything else (a network blip, a 500) leaves the panel exactly as it was
    // and keeps the slow timer: the worker is still chewing through the stores
    // regardless, and the next answer that does arrive corrects the display.

    timerRef.current = setTimeout(poll, nextDelay);
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    poll();

    // Coming back to the tab should show the current state, not whatever was
    // true when it was last in front - which on the idle timer could be half a
    // minute stale, and a run started in between would be invisible until then.
    const onVisibility = () => {
      if (stoppedRef.current || document.hidden) return;
      clearTimer();
      poll();
    };

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stoppedRef.current = true;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [poll, clearTimer]);

  const total = run?.total ?? 0;
  const finishedCount = finishedCountOf(run);

  return {
    run,
    otherActiveRuns,
    finishedCount,
    total,
    // Shown on the collapsed accordion row, so a failure does not need
    // expanding to be noticed.
    failedCount: run?.counts?.failed ?? 0,
    // Stores done out of stores in the run. The only genuine percentage here:
    // a store itself is one job with no inside to measure.
    percent: total ? Math.round((finishedCount / total) * 100) : 0,
  };
};

export default useSyncProgress;
